import React, { useLayoutEffect, useRef, useState, useId } from "react";
import { createPortal } from "react-dom";
import SelectionToolbar from "./SelectionToolbar";
import { normalizePastedHtml, withMarkdownEmphasis, isRichText, escapeText, richTextToPlain, sanitizeRichText, toRichHtml } from "./richText";
import "./richText.css";

// Controlled HTML value, with textarea-compatible target.name/target.value.
// Only external changes replace the DOM, so typing does not reset the caret.
export default function RichTextInput({ value = "", onChange, name, placeholder,
  extraClass = "", className = "", rows = 3, maxHeight = "20rem", maxLength, disabled = false,
  inputRef, editorApiRef, mentionFormat = "nameFirst", mentions = [], ...props }) {
  const ref = useRef(null);
  const caret = useRef(null);
  const suggestionsId = useId();
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const emitted = useRef(null);
  const mentionRange = useRef(null);
  const [query, setQuery] = useState(null);
  const [active, setActive] = useState(0);
  const [isEmpty, setIsEmpty] = useState(() => !richTextToPlain(value).trim());
  const matches = query === null ? [] : mentions.filter((item) =>
    String(item.display ?? item.label).toLowerCase().includes(query.toLowerCase())).slice(0, 10);
  const isMentionOpen = matches.length > 0;
  useLayoutEffect(() => {
    if (!isMentionOpen) return undefined;
    const position = () => {
      const rect = ref.current.getBoundingClientRect();
      const width = Math.min(360, Math.max(200, rect.width), window.innerWidth - 16);
      setMenuPosition({
        position: "fixed", zIndex: 2147483000,
        bottom: window.innerHeight - rect.top + 5,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        width, maxHeight: Math.max(0, Math.min(240, rect.top - 13)),
      });
    };
    const dismiss = (event) => {
      if (!ref.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setQuery(null);
    };
    position();
    window.addEventListener("resize", position);
    document.addEventListener("scroll", position, true);
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("focusin", dismiss);
    return () => {
      window.removeEventListener("resize", position);
      document.removeEventListener("scroll", position, true);
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("focusin", dismiss);
    };
  }, [isMentionOpen]);
  useLayoutEffect(() => {
    const menu = menuRef.current;
    const option = menu?.children[active];
    if (!option) return;
    if (option.offsetTop < menu.scrollTop) menu.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = option.offsetTop + option.offsetHeight - menu.clientHeight;
    }
  }, [active, query, menuPosition]);
  const decorateMentions = () => {
    const walker = document.createTreeWalker(ref.current, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      if (node.parentElement.closest("[data-mention], code, pre")) return;
      const pattern = mentionFormat === "idFirst" ? /@\[(\d+)\]\(([^)]+)\)/g : /@\[([^\]]+)\]\((\d+)\)/g;
      const text = node.textContent;
      let match; let offset = 0;
      const fragment = document.createDocumentFragment();
      while ((match = pattern.exec(text))) {
        fragment.appendChild(document.createTextNode(text.slice(offset, match.index)));
        const chip = document.createElement("span");
        chip.dataset.mention = match[0];
        chip.setAttribute("contenteditable", "false");
        chip.className = "rich-text-mention";
        chip.textContent = `@${match[mentionFormat === "idFirst" ? 2 : 1]}`;
        fragment.appendChild(chip);
        offset = match.index + match[0].length;
      }
      if (offset) {
        fragment.appendChild(document.createTextNode(text.slice(offset)));
        node.replaceWith(fragment);
      }
    });
  };
  useLayoutEffect(() => {
    if (value !== emitted.current) {
      caret.current = null;
      ref.current.innerHTML = toRichHtml(value);
      decorateMentions();
      emitted.current = value;
    }
    setIsEmpty(!richTextToPlain(value).trim());
  }, [value]);

  const emit = () => {
    const copy = ref.current.cloneNode(true);
    copy.querySelectorAll("[data-mention]").forEach((chip) => chip.replaceWith(document.createTextNode(chip.dataset.mention)));
    const html = sanitizeRichText(copy.innerHTML);
    if (maxLength && richTextToPlain(html).length > maxLength) {
      ref.current.innerHTML = toRichHtml(value);
      return;
    }
    emitted.current = richTextToPlain(html).trim() ? (isRichText(html) ? html : `<div>${html}</div>`) : "";
    setIsEmpty(!emitted.current);
    onChange?.({ target: { name, value: emitted.current } });
    const selection = window.getSelection();
    const node = selection?.anchorNode;
    const before = node?.nodeType === Node.TEXT_NODE ? node.textContent.slice(0, selection.anchorOffset) : "";
    const match = /(?:^|\s)@([^\s@]*)$/.exec(before);
    if (match && mentions.length) {
      const range = document.createRange();
      range.setStart(node, selection.anchorOffset - match[1].length - 1);
      range.setEnd(node, selection.anchorOffset);
      mentionRange.current = range;
      setQuery(match[1]); setActive(0);
    } else setQuery(null);
  };

  const insert = (html, rangeOverride) => {
    ref.current.focus();
    const selection = window.getSelection();
    let range = rangeOverride || (selection.rangeCount ? selection.getRangeAt(0) : null);
    if (!range || !ref.current.contains(range.commonAncestorContainer)) {
      range = document.createRange(); range.selectNodeContents(ref.current); range.collapse(false);
    }
    range.deleteContents();
    const fragment = range.createContextualFragment(sanitizeRichText(html));
    const last = fragment.lastChild;
    range.insertNode(fragment);
    if (last) range.setStartAfter(last);
    range.collapse(true); selection.removeAllRanges(); selection.addRange(range);
    emit();
  };
  const addMention = (item) => {
    const display = item.display ?? item.label;
    const id = item.id ?? item.value;
    insert(escapeText(mentionFormat === "idFirst" ? `@[${id}](${display}) ` : `@[${display}](${id}) `), mentionRange.current);
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const bookmark = document.createComment("caret");
    range.insertNode(bookmark);
    decorateMentions();
    range.setStartAfter(bookmark); range.collapse(true);
    selection.removeAllRanges(); selection.addRange(range);
    bookmark.remove();
    setQuery(null);
  };
  const rememberCaret = () => {
    const selection = window.getSelection();
    if (selection?.rangeCount && ref.current.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      caret.current = selection.getRangeAt(0).cloneRange();
    }
  };
  useLayoutEffect(() => {
    if (!editorApiRef) return;
    editorApiRef.current = { insertText: (text) => { insert(escapeText(text), caret.current); rememberCaret(); } };
    return () => { editorApiRef.current = null; };
  });

  return <div className="relative w-full">
    <SelectionToolbar editorRef={ref} onChange={emit} disabled={disabled} />
    <div
      ref={(element) => { ref.current = element; if (inputRef) inputRef.current = element; }}
      role="textbox" aria-label={props["aria-label"] || name || placeholder}
      aria-multiline="true" aria-disabled={disabled}
      aria-autocomplete={mentions.length ? "list" : undefined}
      aria-controls={matches.length ? suggestionsId : undefined}
      aria-activedescendant={matches.length ? `${suggestionsId}-${active}` : undefined}
      contentEditable={!disabled} suppressContentEditableWarning
      tabIndex={disabled ? -1 : 0} data-placeholder={placeholder}
      data-empty={isEmpty} aria-placeholder={placeholder}
      className={`rich-text-content w-full rounded border border-gray-300 bg-white text-gray-800 p-2 focus:outline-none focus:ring-1 focus:ring-brandAccent overflow-y-auto ${className} ${extraClass}`}
      style={{ minHeight: `${Math.max(1, rows) * 1.5 + 1}rem`, maxHeight }}
      onInput={emit}
      onKeyUp={rememberCaret}
      onMouseUp={rememberCaret}
      onBlur={rememberCaret}
      onPaste={(event) => {
        props.onPaste?.(event);
        if (event.defaultPrevented) return;
        event.preventDefault();
        const html = event.clipboardData.getData("text/html");
        const plain = escapeText(event.clipboardData.getData("text/plain"))
          .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
          .replace(/\r?\n/g, "<br>");
        const pasted = normalizePastedHtml(withMarkdownEmphasis(html || plain));
        if (!pasted) {
          if (!ref.current.textContent.trim()) emit();
          return;
        }
        // Clear the browser's empty paragraph/BR placeholder before inserting.
        if (!ref.current.textContent.trim()) ref.current.innerHTML = "";
        insert(pasted);
      }}
      onDrop={(event) => event.preventDefault()}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) return;
        if (matches.length && ["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(event.key)) {
          event.preventDefault(); event.stopPropagation();
          if (event.key === "Escape") setQuery(null);
          if (event.key === "Enter" || event.key === "Tab") addMention(matches[active]);
          if (event.key === "ArrowDown") setActive((active + 1) % matches.length);
          if (event.key === "ArrowUp") setActive((active + matches.length - 1) % matches.length);
        } else props.onKeyDown?.(event);
      }}
    />
    {isMentionOpen && menuPosition && createPortal(<div ref={menuRef} style={menuPosition} id={suggestionsId} role="listbox" aria-label="Mention suggestions" className="border border-gray-300 rounded-lg bg-white shadow-lg overflow-y-auto overscroll-contain text-sm">
      {matches.map((item, index) => <button key={item.id ?? item.value} type="button" role="option" aria-selected={index === active} id={`${suggestionsId}-${index}`} tabIndex={-1}
        className={`flex items-center w-full text-left px-4 py-2 border-b border-gray-200 last:border-b-0 ${index === active ? "bg-gray-600 text-white" : "text-gray-800 hover:bg-gray-100"}`}
        onMouseEnter={() => setActive(index)}
        onMouseDown={(event) => event.preventDefault()} onClick={() => addMention(item)}>
        <span aria-hidden="true" className="w-8 h-8 shrink-0 bg-blue-100 rounded-full flex items-center justify-center mr-3 text-sm font-medium text-blue-600">
          {String(item.display ?? item.label).charAt(0).toUpperCase()}
        </span>
        <span className="truncate">{item.display ?? item.label}</span>
      </button>)}
    </div>, document.body)}
  </div>;
}
